import { Link, useLocation } from "react-router-dom";
import { Bus, Phone, Mail, MapPin, ArrowUp, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import logo from "@/assets/logo.png";
import paymentMethods from "@/assets/paymethods.png";
import paymentMethodsDark from "@/assets/paymethodsdark.png";
import { useTheme } from "@/hooks/useTheme";

export function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const location = useLocation();
  const isHome = location.pathname === "/";
  const { theme } = useTheme();

  // Select appropriate payment method image based on theme
  const currentPaymentMethods = theme === "dark" ? paymentMethodsDark : paymentMethods;

  const quickLinks = [
    { name: "Home", href: isHome ? "#home" : "/" },
    { name: "About Us", href: isHome ? "#about" : "/#about" },
    { name: "Features", href: isHome ? "#features" : "/#features" },
    { name: "Services", href: isHome ? "#services" : "/#services" },
    { name: "Contact", href: "/contact" },
  ];

  const legalLinks = [
    { name: "Terms & Conditions", href: "/terms" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Refund Policy", href: "/refund" },
  ];

  return (
    <footer className="relative bg-card/50 border-t border-border/50 overflow-hidden">
      {/* Background glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer */}
        <div className="py-16">
          {/* Top Row - Regular columns */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-8">
            {/* Brand */}
            <div className="lg:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-6">
                <img 
                  src={logo} 
                  alt="CityLink Logo" 
                  className="h-16 w-auto"
                />
              </Link>
              <p className="text-muted-foreground mb-6">
                {siteConfig.description}
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                {siteConfig.social.facebook && (
                  <a
                    href={siteConfig.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {siteConfig.social.twitter && (
                  <a
                    href={siteConfig.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="Twitter"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {siteConfig.social.instagram && (
                  <a
                    href={siteConfig.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {siteConfig.social.linkedin && (
                  <a
                    href={siteConfig.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {siteConfig.social.tiktok && (
                  <a
                    href={siteConfig.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label="TikTok"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="w-5 h-5"
                    >
                      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                    </svg>
                  </a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-display font-semibold text-lg mb-6 text-foreground">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors animated-underline"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-display font-semibold text-lg mb-6 text-foreground">Legal</h3>
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors animated-underline"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-display font-semibold text-lg mb-6 text-foreground">Contact Info</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-muted-foreground text-sm">
                    {siteConfig.locations.map((location, index) => (
                      <div key={location.id} className="mb-4">
                        <span className="font-medium">{location.name}:</span>
                        <div className="whitespace-pre-line">
                          {location.address}
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-muted-foreground text-sm space-y-2">
                    {siteConfig.locations.map((location, index) => (
                      <div key={location.id}>
                        <div className="font-medium">{location.name}:</div>
                        <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="hover:text-primary transition-colors block">
                          {location.phone}
                        </a>
                      </div>
                    ))}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-muted-foreground text-sm">
                    {Array.from(new Set(siteConfig.locations.map((location) => location.email))).map((email) => (
                      <div key={email} className="mb-4">
                        <a href={`mailto:${email}`} className="hover:text-primary transition-colors">
                          {email}
                        </a>
                      </div>
                    ))}
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Payment Methods - Spanning row */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Empty column for spacing */}
            <div className="hidden lg:block"></div>
            
            {/* Payment Methods - spanning across columns 2-3 */}
            <div className="lg:col-span-2 flex flex-col items-start">
              <h3 className="font-display font-semibold text-lg mb-4 text-foreground">Payment Methods</h3>
              <div className="w-full">
                <div className="relative p-6 rounded-lg bg-background/50 border border-border/50">
                  {/* Fixed container with absolute positioning to force consistent sizing */}
                  <div className="flex justify-center items-center h-24 w-full max-w-md mx-auto">
                    <img 
                      src={currentPaymentMethods} 
                      alt="Accepted payment methods including Visa, Mastercard, and other payment options" 
                      className="max-h-24 max-w-full object-contain opacity-90 hover:opacity-100 transition-all duration-300"
                      style={{
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed text-center">
                  We accept all major credit and debit cards for secure online payments
                </p>
              </div>
            </div>

            {/* Empty column for spacing */}
            <div className="hidden lg:block"></div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm text-center sm:text-left">
            © {new Date().getFullYear()} {siteConfig.companyName}. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            Back to top
            <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
              <ArrowUp className="w-4 h-4" />
            </div>
          </button>
        </div>
      </div>
    </footer>
  );
}
